import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import {
  usersService,
  type CreateStaffUserData,
  type UpdateStaffUserData,
} from '../api/users.service';

function invalidateUsers(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.users.all });
}

export function useUserMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const createUser = useMutation({
    mutationFn: (data: CreateStaffUserData) => usersService.create(data),
    onSuccess: () => {
      invalidateUsers(qc);
      toast.success('Usuário criado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao criar usuário'),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStaffUserData }) => usersService.update(id, data),
    onSuccess: () => {
      invalidateUsers(qc);
      toast.success('Usuário atualizado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao atualizar usuário'),
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: () => {
      invalidateUsers(qc);
      toast.success('Usuário deletado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao deletar usuário'),
  });

  const toggleUserActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => usersService.toggleActive(id, isActive),
    onSuccess: (_data, variables) => {
      invalidateUsers(qc);
      toast.success(`Usuário ${variables.isActive ? 'ativado' : 'desativado'} com sucesso!`);
    },
    onError: (e) => onError(e, 'Erro ao alterar status do usuário'),
  });

  const anonymizeUser = useMutation({
    mutationFn: (id: string) => usersService.anonymize(id),
    onSuccess: () => {
      invalidateUsers(qc);
      toast.success('Usuário excluído permanentemente (anonimizado)');
    },
    onError: (e) => onError(e, 'Erro ao excluir permanentemente'),
  });

  return { createUser, updateUser, deleteUser, toggleUserActive, anonymizeUser };
}
